import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Save, AlertTriangle, Lock, Loader2, Info } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { fetchJson, recomputeYear, yearOptions, errorMessage } from "./shared/configShared";
import { Help, TermLabel, Explained } from "./shared/Help";
import type { GlossaryKey } from "./shared/acaGlossary";

interface ACAPoliciesProps {
  clientId?: number;
}

// Only columns that exist on aca_policies (shared/schemas/aca.ts). Anything
// else would be stripped by the server and silently not saved.
interface PolicyForm {
  method: "monthly" | "look-back";
  measurementStart: string;
  measurementEnd: string;
  adminDays: number;
  stabilityStart: string;
  stabilityEnd: string;
  standardMeasurementMonths: number;
  initialMeasurementMonths: number;
  initialAdminDays: number;
  stabilityMonths: number;
}

const toForm = (p: any): PolicyForm => ({
  method: p?.method === "look-back" ? "look-back" : "monthly",
  measurementStart: p?.measurementStart ? String(p.measurementStart).slice(0, 10) : "",
  measurementEnd: p?.measurementEnd ? String(p.measurementEnd).slice(0, 10) : "",
  adminDays: p?.adminDays ?? 0,
  stabilityStart: p?.stabilityStart ? String(p.stabilityStart).slice(0, 10) : "",
  stabilityEnd: p?.stabilityEnd ? String(p.stabilityEnd).slice(0, 10) : "",
  standardMeasurementMonths: p?.standardMeasurementMonths ?? 12,
  initialMeasurementMonths: p?.initialMeasurementMonths ?? 12,
  initialAdminDays: p?.initialAdminDays ?? 30,
  stabilityMonths: p?.stabilityMonths ?? 12,
});

export default function ACAPolicies({ clientId: propClientId }: ACAPoliciesProps) {
  const { user } = useAuth();
  const clientId = propClientId ?? (user as any)?.clientId;
  const currentYear = new Date().getFullYear();
  const [taxYear, setTaxYear] = useState(currentYear - 1);
  const { toast } = useToast();
  const [form, setForm] = useState<PolicyForm>(toForm(null));
  const [problems, setProblems] = useState<string[]>([]);

  const { data: policy, isLoading } = useQuery<any>({
    queryKey: ["/api/aca/policy", clientId, taxYear],
    queryFn: () => fetchJson(`/api/aca/policy?clientId=${clientId}&year=${taxYear}`),
    enabled: !!clientId,
  });
  const { data: constants } = useQuery<any>({
    queryKey: ["/api/aca/constants", taxYear],
    queryFn: () => fetchJson(`/api/aca/constants?year=${taxYear}`),
  });
  const { data: reportingYears = [] } = useQuery<any[]>({
    queryKey: ["/api/aca/reporting-years", clientId],
    queryFn: () => fetchJson(`/api/aca/reporting-years?clientId=${clientId}`),
    enabled: !!clientId,
  });
  const isLocked = !!reportingYears.find((y) => y.taxYear === taxYear)?.isLocked;

  useEffect(() => { setForm(toForm(policy)); setProblems([]); }, [policy]);

  const update = <K extends keyof PolicyForm>(k: K, v: PolicyForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body: any = { clientId, year: taxYear, method: form.method };
      if (form.method === "look-back") {
        Object.assign(body, {
          measurementStart: form.measurementStart || null,
          measurementEnd: form.measurementEnd || null,
          adminDays: Number(form.adminDays) || 0,
          stabilityStart: form.stabilityStart || null,
          stabilityEnd: form.stabilityEnd || null,
          standardMeasurementMonths: Number(form.standardMeasurementMonths) || 12,
          initialMeasurementMonths: Number(form.initialMeasurementMonths) || 12,
          initialAdminDays: Number(form.initialAdminDays) || 0,
          stabilityMonths: Number(form.stabilityMonths) || 12,
        });
      }
      const res = await apiRequest("PUT", "/api/aca/policy", body);
      const saved = await res.json();
      await recomputeYear(clientId, taxYear);
      return saved;
    },
    onSuccess: () => {
      setProblems([]);
      toast({ title: "Measurement policy saved", description: `${taxYear} results recalculated.` });
      queryClient.invalidateQueries({ queryKey: ["/api/aca/policy"] });
    },
    onError: (e: any) => {
      const list: string[] = e?.details?.problems || [];
      setProblems(list);
      toast({ title: "Not saved", description: errorMessage(e), variant: "destructive" });
    },
  });

  if (!clientId) return <Alert><AlertDescription>No client selected.</AlertDescription></Alert>;
  if (isLoading) return <div className="animate-pulse"><Card><CardContent className="h-64" /></Card></div>;

  const gapDays = form.measurementEnd && form.stabilityStart
    ? Math.round((new Date(form.stabilityStart).getTime() - new Date(form.measurementEnd).getTime()) / 86400000) - 1
    : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Measurement Policy</h2>
          <p className="text-sm text-muted-foreground">How ACA full-time status is determined month by month for {taxYear}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(taxYear)} onValueChange={(v) => setTaxYear(Number(v))}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>{yearOptions().map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || isLocked}>
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save & Recalculate
          </Button>
        </div>
      </div>

      {isLocked && (
        <Alert><Lock className="h-4 w-4" /><AlertDescription>{taxYear} is finalized. Unlock it on the History tab to change the policy; a corrected version will be generated.</AlertDescription></Alert>
      )}

      {problems.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <ul className="list-disc list-inside space-y-1">{problems.map((p, i) => <li key={i}>{p}</li>)}</ul>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle><TermLabel term={form.method === "look-back" ? "lookBack" : "monthlyMeasurement"}>Measurement Method</TermLabel></CardTitle>
            <CardDescription>Employer election under §4980H. Applies to every employee in the tax year.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label><TermLabel term={form.method === "look-back" ? "lookBack" : "monthlyMeasurement"}>Method</TermLabel></Label>
              <Select value={form.method} onValueChange={(v) => update("method", v as PolicyForm["method"])} disabled={isLocked}>
                <SelectTrigger className="w-[260px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly measurement</SelectItem>
                  <SelectItem value="look-back">Look-back measurement</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {form.method === "monthly"
                  ? "Full-time status is determined month by month: 130 or more hours of service in a calendar month means ACA full-time for that month."
                  : "Ongoing employees are measured over a standard measurement period, then treated as full-time (or not) for the following stability period regardless of hours worked in it."}
                {" "}<Help term={form.method === "monthly" ? "lookBack" : "monthlyMeasurement"} text={form.method === "monthly" ? "The alternative, look-back measurement: " + LOOKBACK_TEXT : "The alternative, monthly measurement: " + MONTHLY_TEXT} title={false} />
              </p>
            </div>

            {form.method === "look-back" && (
              <div className="space-y-5 border-t pt-5">
                <div>
                  <div className="text-sm font-medium mb-2">Standard cycle (ongoing employees)</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label><TermLabel term="standardMeasurementPeriod">Standard measurement period start</TermLabel></Label>
                      <Input type="date" value={form.measurementStart} onChange={(e) => update("measurementStart", e.target.value)} disabled={isLocked} />
                    </div>
                    <div className="space-y-1">
                      <Label><TermLabel term="standardMeasurementPeriod">Standard measurement period end</TermLabel></Label>
                      <Input type="date" value={form.measurementEnd} onChange={(e) => update("measurementEnd", e.target.value)} disabled={isLocked} />
                    </div>
                    <div className="space-y-1">
                      <Label><TermLabel term="administrativePeriod">Administrative period (days)</TermLabel></Label>
                      <Input type="number" min={0} max={90} value={form.adminDays} onChange={(e) => update("adminDays", Number(e.target.value))} disabled={isLocked} />
                      <p className="text-xs text-muted-foreground">
                        Gap between measurement end and stability start{gapDays != null ? `: currently ${gapDays} day${gapDays === 1 ? "" : "s"}` : ""}. Max 90.
                      </p>
                    </div>
                    <div />
                    <div className="space-y-1">
                      <Label><TermLabel term="stabilityPeriod">Stability period start</TermLabel></Label>
                      <Input type="date" value={form.stabilityStart} onChange={(e) => update("stabilityStart", e.target.value)} disabled={isLocked} />
                    </div>
                    <div className="space-y-1">
                      <Label><TermLabel term="stabilityPeriod">Stability period end</TermLabel></Label>
                      <Input type="date" value={form.stabilityEnd} onChange={(e) => update("stabilityEnd", e.target.value)} disabled={isLocked} />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Enter the actual dates for the cycle whose stability period covers {taxYear}. Later cycles roll forward by the stability length below.
                  </p>
                </div>

                <div>
                  <div className="text-sm font-medium mb-2">Durations (used to roll cycles forward and to build employee-specific initial periods)</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-1">
                      <Label><TermLabel term="standardMeasurementPeriod">Standard measurement (months)</TermLabel></Label>
                      <Input type="number" min={3} max={12} value={form.standardMeasurementMonths} onChange={(e) => update("standardMeasurementMonths", Number(e.target.value))} disabled={isLocked} />
                    </div>
                    <div className="space-y-1">
                      <Label><TermLabel term="stabilityPeriod">Stability (months)</TermLabel></Label>
                      <Input type="number" min={6} max={12} value={form.stabilityMonths} onChange={(e) => update("stabilityMonths", Number(e.target.value))} disabled={isLocked} />
                    </div>
                    <div className="space-y-1">
                      <Label><TermLabel term="initialMeasurementPeriod">Initial measurement (months)</TermLabel></Label>
                      <Input type="number" min={3} max={12} value={form.initialMeasurementMonths} onChange={(e) => update("initialMeasurementMonths", Number(e.target.value))} disabled={isLocked} />
                    </div>
                    <div className="space-y-1">
                      <Label><TermLabel term="administrativePeriod">Initial administrative (days)</TermLabel></Label>
                      <Input type="number" min={0} max={90} value={form.initialAdminDays} onChange={(e) => update("initialAdminDays", Number(e.target.value))} disabled={isLocked} />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    New hires whose Employment Type is Variable Hour, Seasonal or Part-Time get an initial measurement period starting at hire (a Limited Non-Assessment Period, Line 16 code 2D). New Full-Time hires are measured monthly until they complete a standard measurement period. The classification comes from the employee record; there is no separate toggle. <Help term="employeeClassification" />
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Lock className="h-4 w-4" /> Statutory (read-only)</CardTitle>
              <CardDescription>IRC §4980H and Treasury regulations. Maintained by Administr, not configurable.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row term="fullTime" label="ACA full-time" value={`${constants?.fullTimeHoursPerWeek ?? 30} hrs/week or ${constants?.fullTimeHoursPerMonth ?? 130} hrs/month of service`} />
              <Row term="fteCap" label="FTE hours cap" value={`${constants?.fteHoursCap ?? 120} hrs per non-full-time employee per month`} />
              <Row term="ale" label="ALE threshold" value={`${constants?.aleThreshold ?? 50} average full-time + FTE (prior calendar year)`} />
              <Row term="waitingPeriod" label="Waiting period" value="Coverage must be effective within 90 days of eligibility" />
              <Row term="standardMeasurementPeriod" label="Measurement period" value="3 to 12 months" />
              <Row term="administrativePeriod" label="Administrative period" value="Up to 90 days" />
              <Row term="initialMeasurementPeriod" label="Initial measurement + admin" value="Must end by the end of the 13th full month after hire" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Info className="h-4 w-4" /> Derived from Benefits</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-2">
              <p><Explained term="waitingPeriod"><span className="underline decoration-dotted">Waiting periods</span></Explained> and <Explained term="entryDate"><span className="underline decoration-dotted">coverage entry dates</span></Explained> are read from each health plan's eligibility groups in the Benefits module and are not re-entered here.</p>
              <p><Explained term="offer"><span className="underline decoration-dotted">Offers of coverage</span></Explained>, <Explained term="minimumValue"><span className="underline decoration-dotted">minimum value</span></Explained> and spouse/dependent eligibility come from the plan library and the employer's plan links.</p>
              <div className="pt-1"><Explained term="hoursOfService"><Badge variant="outline">Hours of service: Employees tab</Badge></Explained></div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

const MONTHLY_TEXT = "full-time status is decided each calendar month from that month's hours (130 or more means full-time).";
const LOOKBACK_TEXT = "hours are averaged over a 3 to 12 month measurement period and the result fixes status for the following stability period.";

function Row({ label, value, term }: { label: string; value: string; term?: GlossaryKey }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{term ? <TermLabel term={term}>{label}</TermLabel> : label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}
